/**
 * ONE authenticated byte transport, for every host (#1744, ruled in mailbox
 * comment 54560).
 *
 * ── WHY THIS IS IN THE SDK AND NOT IN A HOST OR IN `packages/ui` ────────────
 * The SDK is already the single consumption boundary. `packages/ui` must never
 * learn a storage route or a token — it renders what it is handed. And a second
 * copy in a second host is precisely how the next file surface reintroduces a
 * transferable link by copying a sibling: `apps/portal/src/data.tsx` had that
 * warning written in its own header, and the console was about to become the
 * sibling.
 *
 * ── AUTHENTICATED GET, NEVER A SIGNED URL ───────────────────────────────────
 * The decision this transport encodes is a MEASUREMENT, not a preference. On
 * the pinned `supabase/storage-api:v1.11.13` (`stack/local/e2e/kb-asset-http.mjs`,
 * 2026-09-01) a signed URL minted by an entitled member was then fetched WITH NO
 * SESSION AT ALL and returned `200 image/png`. That is correct behaviour for a
 * signed URL and exactly what makes it the wrong instrument for private
 * customer content: for its lifetime the link is a transferable bearer
 * capability, so pasting one into a chat, a ticket or a log hands the bytes
 * over. An authenticated GET produces no such artefact — there is nothing to
 * paste. There is deliberately no `sign` path in this file.
 *
 * ── WHAT CHANGED WHEN THE TWO COPIES BECAME ONE ─────────────────────────────
 * The portal's `storageHeaders` fell back to the **anon key** as the bearer when
 * token acquisition failed:
 *
 *     try { token = (await authClient.getAccessToken()) ?? '' }
 *     catch { token = storageAnonKey ?? '' }
 *
 * So a caller whose session had expired did not get a refusal — it got an
 * ANONYMOUS byte read, and whatever the bucket's policy grants `anon` came
 * back. That is a silent downgrade of the caller's identity on the one request
 * where identity is the whole control. This helper refuses instead, which is
 * ruling 54560 point 2 and is the reason the migration is a fix rather than a
 * move.
 *
 * ── A BLOB, NOT A URL ───────────────────────────────────────────────────────
 * `URL.createObjectURL` leaks for the lifetime of the document unless somebody
 * revokes it, and "somebody" was previously each call site. This returns the
 * Blob and leaves URL creation to the host, which is the only layer that knows
 * when its own component unmounts. `objectUrlHolder` below is the small piece
 * of discipline that makes revoking hard to forget.
 */

/* What THIS runtime's `fetch` accepts as a body. Named structurally rather
   than as the DOM's `BodyInit`, because this package compiles with
   `lib: ES2022` + `@types/node` and that global is not in scope. */
type FetchBody = NonNullable<Parameters<typeof fetch>[1]>['body']

/** What the SERVER said the object is. Never composed by a caller. */
export interface StorageTicket {
  bucket: string
  storage_path: string
}

/* The SAME `TokenSource` the PostgREST transport takes, imported rather than
   re-declared: one name, one mechanism. Its header already argues the reason a
   getter beats a captured string — "a token captured at construction is stale
   within the session" — and that argument is if anything sharper here, where a
   stale identity means bytes fetched as somebody the caller no longer is. */
import type { TokenSource } from './transport.js'
export type { TokenSource }

export interface AuthenticatedObjectOptions {
  /** The deployment base URL, e.g. `https://xyz.supabase.co`. */
  baseUrl: string | null | undefined
  token: TokenSource
  /** Supabase requires `apikey` alongside the bearer. It is NOT a credential here. */
  anonKey?: string
  /** Injected in tests. */
  fetchImpl?: typeof fetch
  /**
   * Domain verification, ABOVE the transport (ruling point 4). KB checks the
   * immutable content-type and size facts before a figure renders; other
   * domains keep their own rules. A verifier may only narrow — it is handed the
   * Response and returns the Blob it is prepared to vouch for.
   */
  verify?: (res: Response, ticket: StorageTicket) => Promise<Blob>
}

/**
 * ONE error, for every reason. Absent, expired, forbidden, missing and
 * quarantined must not be distinguishable to a caller — a transport that says
 * which is an existence oracle for private content.
 */
export class ObjectUnavailableError extends Error {
  constructor(message = 'that file is not available') {
    super(message)
    this.name = 'ObjectUnavailableError'
  }
}

/**
 * A path the SERVER produced, and a cheap guard that it still looks like one.
 *
 * This is not the security boundary — storage evaluates the bucket policy as
 * the caller, and the ticket door re-asks visibility before it hands the path
 * over. It is here because a caller-composed key is the shape ruling 54560
 * point 1 forbids, and a traversal segment arriving from anywhere means the
 * caller composed it.
 */
function assertServerPath(ticket: StorageTicket): void {
  const { bucket, storage_path: path } = ticket
  if (!bucket || !path) throw new ObjectUnavailableError()
  if (path.startsWith('/') || path.includes('..') || /^[a-z]+:\/\//i.test(path)
      || bucket.includes('/') || bucket.includes('..')) {
    throw new ObjectUnavailableError()
  }
}

/**
 * The caller's CURRENT token, or a refusal.
 *
 * Shared by the read and the write so the two cannot drift — which they did
 * once already: `apps/portal/src/data.tsx`'s `storageHeaders` fell back to the
 * anon key as the BEARER on `catch`, so an expired session became an anonymous
 * request on exactly the calls where identity is the whole control. That is the
 * one behaviour this function exists to make impossible in both directions.
 */
async function resolveToken(source: TokenSource): Promise<string> {
  let token: string | null | undefined
  try {
    token = typeof source === 'function' ? await source() : source
  } catch {
    throw new ObjectUnavailableError('your session could not be confirmed')
  }
  if (!token) throw new ObjectUnavailableError('your session could not be confirmed')
  return token
}

/**
 * Stream one private object under THIS caller's session and return its bytes.
 *
 * Storage evaluates the bucket's RLS predicate on the byte request itself, so
 * authority is re-decided by the server at fetch time rather than baked into a
 * link at mint time.
 */
export async function fetchAuthenticatedObject(
  ticket: StorageTicket,
  opts: AuthenticatedObjectOptions,
): Promise<Blob> {
  if (!opts.baseUrl) throw new ObjectUnavailableError('this deployment has no file store configured')
  assertServerPath(ticket)

  const token = await resolveToken(opts.token)

  const base = String(opts.baseUrl).replace(/\/$/, '')
  const doFetch = opts.fetchImpl ?? globalThis.fetch
  const res = await doFetch(
    `${base}/storage/v1/object/authenticated/${ticket.bucket}/${ticket.storage_path}`,
    {
      method: 'GET',
      headers: {
        apikey: opts.anonKey ?? '',
        authorization: `Bearer ${token}`,
      },
    },
  )
  if (!res.ok) throw new ObjectUnavailableError()
  return opts.verify ? opts.verify(res, ticket) : res.blob()
}

/**
 * A document-scoped object URL that cannot be forgotten.
 *
 * Every host needs the same three behaviours — revoke on replacement, on a
 * context change, and on unmount — and each one written by hand is a leak
 * waiting for the case nobody thought about. `set` revokes what it replaces and
 * `release` revokes the last one, so a component's cleanup is one call.
 */
export function objectUrlHolder(): {
  set(blob: Blob): string
  current(): string | null
  release(): void
} {
  let url: string | null = null
  return {
    set(blob) {
      if (url) URL.revokeObjectURL(url)
      url = URL.createObjectURL(blob)
      return url
    },
    current: () => url,
    release() {
      if (url) URL.revokeObjectURL(url)
      url = null
    },
  }
}

/**
 * Put one private object's bytes, under THIS caller's session (#1744 point 4).
 *
 * ── THE ORDER IS NOT A CONVENTION, IT IS ENFORCED ───────────────────────────
 * The bytes go SECOND. Every private bucket in this estate installs the same
 * insert policy (`core.ensure_private_bucket`), and the module's own callback
 * decides — for KB assets, `service.kb_asset_object_authorized` admits an
 * `insert` only when a `service.kb_asset` row ALREADY EXISTS at that exact key,
 * is still `pending`, and the caller holds write authority on the article.
 *
 * So a caller cannot choose a key, cannot upload to a row that does not exist,
 * and cannot upload twice: once the relay verifies the bytes the row leaves
 * `pending` and the insert arm goes false for ever. Replacement is a new
 * revision at a new server-minted key, never a rewrite of this one.
 *
 * `x-upsert: false` is belt FOR KB, and it is the whole strap somewhere else —
 * a distinction worth the sentence, because "redundant" is the reason a future
 * reader deletes a header.
 *
 *   · KB (`service.kb_asset_object_authorized`) and project files
 *     (`afd/2026081919`) return `false` for `update_existing` and `update_new`.
 *     There the policy already refuses an overwrite and the header only makes a
 *     misconfigured bucket refuse it too.
 *   · **`engagement-files` GRANTS both** (`engagements/2026081904:37-43`) — to
 *     the uploader, or to anyone with write on the engagement. For that bucket,
 *     one of this transport's two live callers, the header is the ONLY thing
 *     refusing an overwrite, and the call-site comment it replaced said so:
 *     *"Never overwrite. A key collision would mean two rows disagreeing about
 *     the same bytes."* Do not remove it.
 *
 * ── DELETE EXISTS, BUT NOT FOR KB — and that distinction is the module's ────
 * I first wrote here that there is no delete at all. That was wrong, and it
 * generalised one module's rule to the estate: `core.ensure_private_bucket`
 * installs FOUR policies — select, insert, update and delete — each delegating
 * to the owning module's callback. Engagement and project files legitimately
 * delete, and have governed doors for it.
 *
 * It is KB specifically whose callback answers `false` for anything that is not
 * `read` or `insert`: a stored figure is evidence, and the way to stop serving
 * it is to supersede its row, not to reach for the bytes. So
 * `deleteAuthenticatedObject` below is real and used — just never by KB.
 */
async function putObject(
  ticket: StorageTicket,
  body: Blob | ArrayBuffer | Uint8Array,
  opts: AuthenticatedObjectOptions & { contentType: string },
  upsert: boolean,
): Promise<void> {
  if (!opts.baseUrl) throw new ObjectUnavailableError('this deployment has no file store configured')
  assertServerPath(ticket)
  if (!opts.contentType) throw new ObjectUnavailableError('a stored object must declare its type')

  const token = await resolveToken(opts.token)
  const base = String(opts.baseUrl).replace(/\/$/, '')
  const doFetch = opts.fetchImpl ?? globalThis.fetch
  const res = await doFetch(
    `${base}/storage/v1/object/${ticket.bucket}/${ticket.storage_path}`,
    {
      method: 'POST',
      headers: {
        apikey: opts.anonKey ?? '',
        authorization: `Bearer ${token}`,
        'content-type': opts.contentType,
        /* NOT redundant. For `engagement-files` the bucket policy PERMITS an
           overwrite by the uploader, so this header is the refusal — see the
           file header. For KB and project files it is belt. */
        'x-upsert': String(upsert),
      },
      body: body as FetchBody,
    },
  )
  // ONE error, for every reason — the same rule the read holds to. "Refused",
  // "already there" and "no such bucket" must not be distinguishable to a
  // caller who may not know which of them is true.
  if (!res.ok) throw new ObjectUnavailableError('those bytes could not be stored')
}

export async function putAuthenticatedObject(
  ticket: StorageTicket,
  body: Blob | ArrayBuffer | Uint8Array,
  opts: AuthenticatedObjectOptions & { contentType: string },
): Promise<void> {
  return putObject(ticket, body, opts, false)
}

/**
 * Put provisional KB bytes, allowing an interrupted PENDING upload to resume.
 *
 * This is deliberately a separate verb rather than an `upsert` option on the
 * generic transport: engagement/project evidence must never acquire overwrite
 * semantics because a caller toggled a boolean. The service bucket itself
 * admits this only while the governed row is pending AND upload completion has
 * not been recorded. `complete_kb_asset_upload` closes the window before the
 * verifier event exists; verified and quarantined objects remain immutable.
 */
export async function putPendingKbAssetObject(
  ticket: StorageTicket,
  body: Blob | ArrayBuffer | Uint8Array,
  opts: AuthenticatedObjectOptions & { contentType: string },
): Promise<void> {
  return putObject(ticket, body, opts, true)
}

/**
 * SHA-256 of what is about to be stored, as lowercase hex.
 *
 * ── IT REFUSES RATHER THAN DEGRADES, AND THAT IS DELIBERATE ─────────────────
 * `apps/portal/src/data.tsx`'s `sha256Hex` returns NULL on a non-secure origin,
 * on the argument that "the upload still happens, and the row carries no hash
 * rather than a wrong one". That is right for a portal file, whose row records
 * an uploader's assertion.
 *
 * It is wrong for a KB asset. `service.attach_kb_asset` hard-requires a 64-hex
 * digest, and the relay later compares the bytes it fetched against it — the
 * hash is not decoration there, it is the thing that makes a verdict possible.
 * A null would be refused by the door anyway; refusing here says why.
 */
export async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) {
    throw new ObjectUnavailableError(
      'this browser cannot hash the file — a secure origin (https or localhost) is required to attach one')
  }
  /* An `ArrayBuffer` goes straight in — the only shape the one caller passes,
     and no copy. A VIEW is copied, and that is not defensiveness: `BufferSource`
     deliberately excludes a view over a `SharedArrayBuffer`, so handing the view
     through needs a cast that would be a lie in exactly the case the copy
     handles. `new Uint8Array(view)` also resolves `byteOffset`/`byteLength`
     honestly, which the earlier `view.buffer as ArrayBuffer` branch got right
     only by having a second branch to get right. The copy costs far less than
     the hash it feeds. */
  const digest = await subtle.digest(
    'SHA-256', bytes instanceof Uint8Array ? new Uint8Array(bytes) : bytes)
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Delete one private object, under THIS caller's session.
 *
 * Only where the owning module's bucket callback permits it — engagement and
 * project files do; KB assets do not, by design (see above). The governed row
 * is deleted through its own door FIRST; this removes the bytes the row was
 * pointing at.
 *
 * ── IT DOES NOT SWALLOW ITS FAILURE, AND THAT IS THE CHANGE ─────────────────
 * The two call sites this replaces ended in `.catch(() => undefined)`. Since
 * the governed row is already gone by then, a silently-failed byte delete
 * leaves an orphaned object that nothing will ever mention again — the one
 * place the old anon-key fallback produced a wrong OUTCOME rather than a
 * visible error. A caller that genuinely wants to proceed regardless can catch
 * it; it must be their sentence, not this function's.
 */
export async function deleteAuthenticatedObject(
  ticket: StorageTicket,
  opts: AuthenticatedObjectOptions,
): Promise<void> {
  if (!opts.baseUrl) throw new ObjectUnavailableError('this deployment has no file store configured')
  assertServerPath(ticket)
  const token = await resolveToken(opts.token)
  const base = String(opts.baseUrl).replace(/\/$/, '')
  const doFetch = opts.fetchImpl ?? globalThis.fetch
  const res = await doFetch(
    `${base}/storage/v1/object/${ticket.bucket}/${ticket.storage_path}`,
    { method: 'DELETE', headers: { apikey: opts.anonKey ?? '', authorization: `Bearer ${token}` } },
  )
  if (!res.ok) throw new ObjectUnavailableError('those bytes could not be removed')
}

/**
 * The headers an authenticated storage request must carry — bearer resolved
 * fresh, and REFUSING rather than falling back.
 *
 * Exported for the one legitimate case that cannot use the helpers above: a
 * caller that must read the HTTP STATUS. `apps/portal`'s case-evidence attach
 * treats `409` on a resume as success — the bytes are already there — and the
 * helpers deliberately collapse every failure into one non-oracular error, so
 * routing that call through them would turn a successful resume into a failure.
 *
 * The DEFECT this exists to retire is the fallback, not the fetch. A host may
 * keep its own request when it needs the status; it may not keep its own idea
 * of what to do when the token is gone.
 */
export async function authenticatedStorageHeaders(
  opts: Pick<AuthenticatedObjectOptions, 'token' | 'anonKey'>,
  extra: Record<string, string> = {},
): Promise<Record<string, string>> {
  const token = await resolveToken(opts.token)
  /* `extra` FIRST. Spread last, a caller could pass its own `authorization` and
     put back exactly the thing this function exists to remove — which the
     paragraph above forbids in prose and the spread order permitted. */
  return { ...extra, apikey: opts.anonKey ?? '', authorization: `Bearer ${token}` }
}
